import CONSTANTS from 'src/utils/constants';
import {
    CDTSegmentType,
    ColumnDataType,
    ColumnDef,
    ColumnSourceType,
    DerivedConfig,
    PeriodBehavior,
    PullStatusResponse,
    SearchSegmentDataTableRowsResponse,
    SegmentAggregates,
    SegmentPeriodInfo,
    SegmentPullCriteria,
    SegmentTableDefinition,
} from 'src/models/segment-cdt/SegmentCdtTypes';
import ReactTableFilter from 'src/models/common/ReactTableFilter';

/** Rows per page in the detail data-viewer; mirrors the CLI custom-data-tables records grid. */
export const SEGMENT_DETAIL_PAGE_SIZE = 50;

/** A page of segment rows plus the client-derived `pagesCount` a numbered pager needs. */
export type SegmentRowsPage = SearchSegmentDataTableRowsResponse & { pagesCount: number };

/** In-progress config for the single column currently being added. */
export interface SegmentColumnDraft {
    displayName?: string;
    // Create wizard builds MANUAL | PULL_GL; the detail-page Add Columns modal also builds the two
    // DERIVED_* kinds, which carry a derivedConfig (and, for DERIVED_BAL, a pullCriteria).
    sourceType?: ColumnSourceType;
    dataType?: ColumnDataType;
    currency?: string;
    pullCriteria?: SegmentPullCriteria;
    derivedConfig?: DerivedConfig;
    // MANUAL columns only: whether entered values persist into later periods. Optional - left unset the
    // column is stored without the option, which is what every column created before it existed looks like.
    periodBehavior?: PeriodBehavior;
    // NUMBER columns only: the Common Balance Pool join default. Held as the raw input string so a
    // mid-edit value like "-" or "1." is preserved while typing; parsed and validated at build time.
    defaultValue?: string;
}

/**
 * State for the Create Segment Data Table wizard. Resettable as a unit on open/close.
 * `columns` array order IS `value_order` (immutable after create). `columnEditor` is the
 * add-column sub-form; when absent the column list is shown.
 */
export interface CreateWizardState {
    visible: boolean;
    currentStep: number; // 1 = meta, 2 = columns
    tableName: string; // seeded with the CDT name prefix
    segmentType?: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    columns: ColumnDef[];
    // True while the column-type chooser is shown (before an editor opens). Held here — not in local
    // component state — so the whole flow resets with the slice on open/close.
    choosing: boolean;
    // `error` is the refusal `buildColumnDef` returned for this draft, if any. It lives beside the draft
    // rather than in the form's own state so the reducer can drop it the moment the draft changes — the
    // message names the field it refused, so an edit makes it untrue (see SET_COLUMN_DRAFT).
    columnEditor?: { draft: SegmentColumnDraft; error?: string };
    creating: boolean;
    createError: string;
}

/**
 * State for the detail (data-viewer) route: the table's definition + the period being viewed and the
 * current page/sort of its rows. Row data itself is the reducer-held `searchResult` (set by the search
 * hook), mirroring the CLI records view. `periods` comes from GetSegmentPeriods (empty until that op
 * ships); `selectedPeriod` defaults to the latest.
 */
export interface SegmentDetailState {
    // The table id being viewed, decoded from the route param. Held here (not just passed as a prop) so
    // the toolbar and its modals read it from context — reliable and available immediately, unlike
    // `definition?.tableId`, which is null until the definition GET resolves.
    tableId?: string;
    definition?: SegmentTableDefinition;
    periods: SegmentPeriodInfo[];
    selectedPeriod?: string;
    page: number; // 1-based, matching the backend's numbered paging
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    // Per-column "filter rows" values (react-table shape); built into the search filterExpression.
    filters: ReactTableFilter[];
    // Bumped after a write (GL pull / upload) to force the current period's rows to re-fetch.
    refreshToken: number;
    searchResult?: SegmentRowsPage;
    // Server-computed per-column totals for the selected period (GetSegmentAggregates). Re-fetched on
    // the same refreshToken as the rows, so a GL pull or upload updates the totals with them.
    aggregates?: SegmentAggregates;
    // The GL pull being tracked for the selected period, as last polled by the toolbar. Held here rather
    // than in the toolbar's local state so the metadata row can report progress from the SAME poll -
    // a second poller would double the request rate and could disagree with the button it sits beside.
    // Undefined = no pull is being tracked (idle, or the period has no pull in flight).
    pullStatus?: PullStatusResponse;
    // The column whose configuration popup is open; undefined = closed. Held in the slice rather than in the
    // view's own state because it is a snapshot OF the definition, so it has to be invalidated with it: both
    // a period switch and a definition replacement (AddSegmentColumns) clear it, which local state could not
    // do without the view watching for those itself.
    configColumn?: ColumnDef;
}

/**
 * State for the Segment Data Tables feature. `refreshToken` drives the list re-fetch (bumped after
 * a create); `createWizard` holds the create flow; `detail` holds the data-viewer route. List/row data
 * itself lives in the service hook tuples. The list tab and the detail route are separate mounts, so
 * each gets its own reducer instance — the slices never coexist in one live store.
 */
export interface SegmentTablesState {
    refreshToken: number;
    createWizard: CreateWizardState;
    detail: SegmentDetailState;
}

export const initialCreateWizardState: CreateWizardState = {
    visible: false,
    currentStep: 1,
    tableName: CONSTANTS.CUSTOM_DATA_TABLE_PREFIX,
    columns: [],
    choosing: false,
    creating: false,
    createError: '',
};

// Shared module constant: OPEN/CLOSE_CREATE_WIZARD spread it, so its `columns` array reference is
// handed to every wizard. Freeze the object and that array so an accidental in-place mutation fails
// loudly instead of silently leaking across wizard sessions. Every reducer path already builds new
// arrays, so this only guards against future edits.
Object.freeze(initialCreateWizardState.columns);
Object.freeze(initialCreateWizardState);

export const initialDetailState: SegmentDetailState = {
    periods: [],
    page: 1,
    pageSize: SEGMENT_DETAIL_PAGE_SIZE,
    filters: [],
    refreshToken: 0,
};
// Frozen like the wizard state: every reducer path builds new objects, so this only guards against a
// future in-place mutation of the shared initial slice.
Object.freeze(initialDetailState.periods);
Object.freeze(initialDetailState.filters);
Object.freeze(initialDetailState);

export const initialState: SegmentTablesState = {
    refreshToken: 0,
    createWizard: initialCreateWizardState,
    detail: initialDetailState,
};
Object.freeze(initialState);
